export enum AiScribeStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  NEW = 'NEW',
  START_AUDIO_PROCESSING = 'START_AUDIO_PROCESSING',
  START_AUDIO_REPROCESS = 'START_AUDIO_REPROCESS',
  FAILED_QUEUE_CONSUMPTION = 'FAILED_QUEUE_CONSUMPTION',
  REPROCESS_AUDIO = 'REPROCESS_AUDIO',
  FAILED_AUDIO_PROCESSING = 'FAILED_AUDIO_PROCESSING',
  // Transcription is done and the job is waiting for the user to manually kick
  // off the Transcription Summary (non-PR Session/Dictation/Measurement scribes).
  COMPLETE_TRANSCRIPTION_STEP = 'COMPLETE_TRANSCRIPTION_STEP',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  ERROR = 'ERROR',
  START_AI_REPORT_GENERATION = 'START_AI_REPORT_GENERATION',
  QUEUED_AI_REPORT_GENERATION = 'QUEUED_AI_REPORT_GENERATION',
  START_LLM_GENERATION = 'START_LLM_GENERATION',
  COMPLETE_AI_REPORT_GENERATION = 'COMPLETE_AI_REPORT_GENERATION',
  REPROCESS_AI_REPORT_GENERATION = 'REPROCESS_AI_REPORT_GENERATION',
  FAILED_AI_REPORT_GENERATION = 'FAILED_AI_REPORT_GENERATION',
  DISCARDED = 'DISCARDED',
  APPENDED = 'APPENDED',
  pending = 'pending',
  START_AI_REPORT_GENERATION_REPROCESS = 'START_AI_REPORT_GENERATION_REPROCESS',
}

/** Mirrors the backend's `AiScribeContentOutputTypes` (`ai-scribe-content-output-type.constants.ts`). */
export type AiScribeContentOutputType =
  | 'LEGACY'
  | 'TRANSCRIPT'
  | 'PR1'
  | 'PR2'
  | 'PR4'
  // Sibling documents of a PR report. Same scribe job, own content row, own
  // DOCX, own tab; none of their content appears in the PR report.
  | 'RFA'
  | 'INJECTION'
  | 'WORK_STATUS'
  | 'GENERAL';

/**
 * The sibling-form content types — every row that is a document of its own
 * next to the PR report, never the PR report itself.
 *
 * The ONE list. selectPrimaryScribeContent keeps these out of the PR tab, and
 * SiblingReportConfig.outputType is typed to it, so a new sibling form that is
 * given a tab cannot be forgotten here.
 */
export const SIBLING_CONTENT_OUTPUT_TYPES = [
  'RFA',
  'INJECTION',
  'WORK_STATUS',
] as const satisfies readonly AiScribeContentOutputType[];

export type SiblingContentOutputType =
  (typeof SIBLING_CONTENT_OUTPUT_TYPES)[number];

export function isSiblingContentOutputType(
  value: unknown,
): value is SiblingContentOutputType {
  return (
    typeof value === 'string' &&
    (SIBLING_CONTENT_OUTPUT_TYPES as readonly string[]).includes(value)
  );
}

export interface AiScribeContents {
  id?: string;
  outputType?: AiScribeContentOutputType;
  /** Increments each time a new content row is created for the same outputType (e.g. on regeneration). */
  version?: number;
  inputText?: string;
  generatedOutput?: {
    /**
     * HTML (PR reports) or markdown (summaries).
     *
     * An RFA row actually carries a JSON OBJECT here, not a string — but this
     * stays declared as `string` on purpose. No frontend path reads an RFA
     * row's output (the RFA tab needs only `id`, since the DOCX is the
     * artifact), and selectPrimaryScribeContent keeps RFA rows out of every
     * consumer that does read it. Widening it to `string | object` instead
     * pushed a non-string onto ~a dozen call sites that legitimately do
     * `.trim()` on it or feed it to a `string` @Input, for no gain.
     *
     * So: only read this off a row you know is NOT the RFA row.
     *
     * Nullable because SessionStore.setAiScribeOutputById clears it with a
     * literal `null` when the user edits the transcription (home.component
     * "clearAiScribeOutput" path).
     */
    output: string | null;
  };
  inputS3Key?: string | null;
  inputS3Bucket?: string | null;
  outputS3Key?: string | null;
  outputS3Bucket?: string | null;
  // Row timestamps from the ai_scribe_content table. `updatedAt` is bumped to
  // `now()` on every output write (initial generation, regeneration, and edits),
  // so it — not the job's createdAt — is the correct "generated/completed at"
  // time to surface in the UI.
  createdAt?: Date | string | null;
  updatedAt?: Date | string | null;
}

/**
 * operations.ai_scribe.rfa_status — the RFA workflow's state, projected onto
 * the scribe so the RFA tab renders without a Temporal lookup.
 *
 * `null`/`undefined` is a FIRST-CLASS, PERMANENT state: no async RFA workflow
 * has evaluated this scribe. It covers every scribe that predates the async
 * flow AND every scribe still on the inline RFA path, where the RFA lives
 * inside the PR report's HTML. The RFA tab is hidden entirely for it, which is
 * exactly today's behaviour.
 *
 * WARNING: switch on the VALUE, never on truthiness — 'NOT_REQUIRED' is truthy,
 * so an `if (rfaStatus)` / `if (!rfaStatus)` pair merges "not requested" with
 * "legacy, hide the tab".
 */
export const SUB_REPORT_STATUS = {
  NOT_REQUIRED: 'NOT_REQUIRED',
  GENERATING: 'GENERATING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

export type SubReportStatus =
  (typeof SUB_REPORT_STATUS)[keyof typeof SUB_REPORT_STATUS];

/**
 * @deprecated Use SUB_REPORT_STATUS. Retained so existing RFA call sites keep
 * compiling through the transition; the values are identical.
 */
export const AI_SCRIBE_RFA_STATUS = SUB_REPORT_STATUS;

/** @deprecated Use SubReportStatus. */
export type AiScribeRfaStatus = SubReportStatus;

export function isSubReportStatus(value: unknown): value is SubReportStatus {
  return (
    typeof value === 'string' &&
    (Object.values(SUB_REPORT_STATUS) as string[]).includes(value)
  );
}

/**
 * selectPrimaryScribeContent is generic over the row shape rather than pinned
 * to `AiScribeContents`.
 *
 * It reads exactly one property — `outputType` — so requiring the full
 * interface bought nothing and cost a lot: several report components (ptp,
 * medical-legal) cast the job to their own narrower defensive shape,
 * `{ generatedOutput?: { output?: string } }`, which is not assignable to
 * `AiScribeContents`. Being generic returns the caller's own type, so
 * `?.generatedOutput?.output` keeps its original type at each call site.
 *
 * The constraint is `object`, deliberately, NOT `{ outputType?: string }` —
 * that would be a *weak type* (every property optional), and TS rejects an
 * argument sharing no properties with it, which is exactly the ptp /
 * medical-legal shape. Hence the internal cast to read `outputType`.
 */

/** Narrowing read of the two fields this selector cares about. */
type WithOutputType = { outputType?: string; version?: number };

/**
 * The PR-report content row — "the row that isn't a sibling form".
 *
 * A scribe job can now own several content rows. `output_type` stays 'LEGACY'
 * for PR reports and is never backfilled, so excluding the sibling forms (RFA,
 * Injection, Work Status — SIBLING_CONTENT_OUTPUT_TYPES) is sufficient and no
 * caller downstream of this has to learn about output types. Excluding RFA
 * alone is NOT: a Work Status or Injection row would then compete for the PR
 * tab exactly the way the RFA row did before it was excluded.
 *
 * Replaces a bare `contents[0]`: the API include has no ORDER BY, so once a
 * scribe owned both a LEGACY and an RFA row, `[0]` was arbitrary and the PR tab
 * could render the RFA's JSON payload. Rows that predate output_type carry no
 * `outputType` at all, so they still match and single-row behaviour is
 * unchanged.
 *
 * Sibling rows of the SAME outputType are possible (PR1/PR2/RFA append a new
 * version on every regeneration), so a superseded row is filtered out before
 * the pick — see the version guard in the body.
 *
 * There is deliberately NO `?? list[0]` fallback. Its only reachable case is
 * "every row is an RFA row", which is a real window rather than a
 * hypothetical: PR-1 now starts its RFA child BEFORE persisting its own
 * output, so a fast RFA (or a retrying PR-1 persist) can leave the scribe
 * holding an RFA row and nothing else. Falling back there would hand the PR
 * report tab the RFA's JSON object, and would make isAiScribeSummaryMissing
 * stringify it to '[object Object]' and report the summary as ready — which
 * unblocks QME/PI/SIBTF/PTP report generation on a scribe that has not
 * produced a report yet. Returning undefined instead leaves the caller with
 * the empty-content shape it already handles.
 */
export function selectPrimaryScribeContent<T extends object>(
  contents: T[] | T | undefined | null,
): T | undefined {
  if (!contents) return undefined;
  const list = Array.isArray(contents) ? contents : [contents];
  const candidates = list.filter(
    (c) => !isSiblingContentOutputType((c as WithOutputType)?.outputType),
  );
  if (candidates.length < 2) return candidates[0];

  // Sibling guard. Every DOCX-backed report type (PR1, PR2, RFA) APPENDS on
  // regeneration, so a scribe can hold several rows of one outputType and the
  // API include has no ORDER BY. "First non-RFA" alone could therefore hand
  // back a superseded version. Drop any row that a same-type row outranks,
  // then keep the original array-order semantics for choosing BETWEEN types
  // (which is what preserves today's LEGACY-vs-PR behaviour).
  const highestVersionByType = new Map<string, number>();
  for (const c of candidates) {
    const { outputType, version } = c as WithOutputType;
    const key = outputType ?? '';
    const v = version ?? 0;
    if (v > (highestVersionByType.get(key) ?? -Infinity)) {
      highestVersionByType.set(key, v);
    }
  }
  return candidates.find((c) => {
    const { outputType, version } = c as WithOutputType;
    return (version ?? 0) === highestVersionByType.get(outputType ?? '');
  });
}

/**
 * "Has this content row produced a summary body?"
 *
 * The session payload deliberately no longer carries `generatedOutput` — a
 * scribe owns several content rows now and shipping every body on every
 * websocket refresh is what made the page hang. So a string check alone is no
 * longer a valid emptiness test: a row with a perfectly good summary arrives
 * with no body at all, and every caller that asked
 * `!content.generatedOutput?.output` would call it missing.
 *
 * Three signals, any one of which means "generated", in cost order:
 *
 *   1. output S3 refs — the API writes outputS3Key/Bucket only when it
 *      offloads a NON-EMPTY body, so their presence is the authoritative
 *      "there is something" flag and it survives the trimmed payload.
 *   2. a PR-family outputType — those rows are the report itself; their body
 *      is a JSON object, so a string check would never see them.
 *   3. a non-empty string body — still true wherever the body IS present
 *      (the lazily-hydrated active scribe, and the untrimmed
 *      GET .../ai-scribe/contents response).
 */
export function hasGeneratedScribeOutput(
  content:
    | {
        outputType?: string | null;
        outputS3Key?: string | null;
        outputS3Bucket?: string | null;
        generatedOutput?: { output?: unknown } | null;
      }
    | null
    | undefined,
): boolean {
  if (!content) return false;
  if (content.outputS3Key && content.outputS3Bucket) return true;
  if (
    typeof content.outputType === 'string' &&
    PR_FAMILY_CONTENT_OUTPUT_TYPES.has(content.outputType)
  ) {
    return true;
  }
  const output = content.generatedOutput?.output;
  return typeof output === 'string' && output.trim().length > 0;
}

/** Report rows whose body is a JSON object rather than a renderable string. */
export const PR_FAMILY_CONTENT_OUTPUT_TYPES: ReadonlySet<string> =
  new Set<AiScribeContentOutputType>(['PR1', 'PR2', 'PR4']);

export interface FileAsset {
  id: string;
  bucketName: string;
  objectKey: string;
  fileExtension: string | null;
  durationSeconds: number | null;
  uploadedBy: string | null;
  createdAt: Date | null;
  // Set when this asset was derived from another (e.g. an MP3 converted from a
  // DSS upload). Transcription retries must follow this back to the original.
  derivedFromFileId?: string | null;
}

export interface AiScribeJob {
  /** See AiScribeRfaStatus — null/undefined means "hide the RFA tab". */
  rfaStatus?: AiScribeRfaStatus | null;
  sessionId?: string;
  aiScribeId: string;
  /**
   * The PR-report content row. Collapsed from the API's array by
   * selectPrimaryScribeContent — see mapSourceToAiScribeJob.
   */
  AiScribeContents: AiScribeContents;
  jobName?: string;
  speciality?: string;
  scribeType?: string | null;
  languageCode?: string | null;
  status?: AiScribeStatus;
  retryCount?: number | null;
  createdAt?: Date;
  updatedAt?: Date;
  FileAsset?: FileAsset | null;
  recordingFileId?: string | null;
  formType?: string | null;
  /**
   * The id of this job's latest AiScribeContent row for each outputType (the
   * highest `version` when a type has been regenerated), used as the
   * generated-document editor's sourceArtifactId. A type is absent while the
   * job hasn't produced a content row of that type yet (e.g. still on the
   * legacy generation path for a PR report type).
   */
  contentIdByOutputType?: Partial<Record<AiScribeContentOutputType, string>>;

  subReportStatuses?: Partial<
    Record<AiScribeContentOutputType, SubReportStatus>
  >;

  /**
   * error_code of each FAILED sub-report row, keyed like subReportStatuses.
   * The worker's failure type — read it through getSubReportFailureReason.
   */
  subReportErrorCodes?: Partial<Record<AiScribeContentOutputType, string>>;
}

export interface AiScribeLogEntry {
  status?: string;
  createdTime?: string | Date;
  createdAt?: string | Date;
}

// A type for the incoming data from api
/**
 * One `ai_scribe_sub_report_statuses` row as the API returns it.
 *
 * Rows, not a map — the API deliberately does not shape this, for the same
 * reason it does not shape AiScribeContents: the collapse lives in
 * mapSourceToAiScribeJob so there is one place that knows which vocabulary is
 * which. `outputType` here is the UPPER-CASE content spelling ('RFA'), not the
 * lower-case report-kind spelling.
 */
export interface SourceSubReportStatus {
  outputType?: string | null;
  status?: string | null;
  errorCode?: string | null;
  updatedAt?: string | Date | null;
}

export interface SourceAiScribeJob {
  /** @deprecated Read subReportStatuses.RFA instead; kept while the API dual-writes. */
  rfaStatus?: AiScribeRfaStatus | null;
  /** Sibling-form lifecycle rows. Always returned by the scribe-list route. */
  subReportStatuses?: SourceSubReportStatus[];
  aiScribeId: string;
  AiScribeContents: AiScribeContents[];
  jobName: string;
  speciality?: string;
  scribeType?: string | null;
  languageCode?: string | null;
  status?: AiScribeStatus;
  retryCount?: number | null;
  createdAt?: Date;
  updatedAt?: Date;
  FileAsset?: FileAsset | null;
  recordingFileId?: string | null;
  latestStatus?: string | null;
  formType?: string | null;
  // Dual-cased on the wire: `AiScribeLogs` from the GraphQL-shaped session
  // hydration payload, `ai_scribe_logs` from the REST `includes` path.
  AiScribeLogs?: AiScribeLogEntry[];
  ai_scribe_logs?: AiScribeLogEntry[];
}

export interface NewAiScribeCreated {
  aiScribe: AiScribeJob;
  aiScribeContents: AiScribeContents;
}
